"use client";

import React from "react";
import { Loader } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Link } from "@/components/common/Link";
export default function PlanYourGathering() {
  return <section data-section-id="224087" id="reach-out-to-us" className="bg-background text-foreground py-24 px-4 md:px-8 lg:px-12 relative overflow-hidden">
      {}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-3xl h-full bg-primary/5 blur-[120px] pointer-events-none rounded-full" />

      <div className="max-w-2xl mx-auto relative z-10">
        <div className="text-center mb-16">
          <h2 className="font-default text-4xl md:text-5xl font-medium tracking-tight mb-4 text-foreground">Book Your Professional RMT &amp; Facial Treatment in North York | Yonge Sheppard Wellness</h2>
          <p className="font-default text-muted-foreground text-lg max-w-lg mx-auto">Have questions about our RMT or Facial services? We are conveniently located at the Yonge and Sheppard intersection, just steps from the Sheppard-Yonge Subway Station, serving the North York and Willowdale community.</p>
        </div>

        <FormProvider formKey="contact_form_contact" formId="contact_form_contact" sectionName="Plan Your Gathering" defaultValues={{
        full_name: "",
        email: "",
        phone: "",
        event_date: "",
        guest_count: 1,
        event_type: "",
        special_requests: ""
      }} validationRules={{
        full_name: {
          required: "Full name is required"
        },
        email: {
          required: "Email is required",
          pattern: {
            value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
            message: "Please enter a valid email address"
          }
        },
        phone: {
          required: "Phone number is required",
          pattern: {
            value: /^[0-9\-\+\(\)\s]+$/,
            message: "Please enter a valid phone number"
          }
        },
        event_date: {
          required: "Preferred appointment date is required"
        },
        guest_count: {
          required: "Number of people is required",
          min: {
            value: 1,
            message: "Minimum 1 person required"
          },
          max: {
            value: 10,
            message: "Maximum 10 people allowed"
          }
        },
        event_type: {
          required: "Please select a service category"
        }
      }} submitText="Send Message" successMessage="Thank you! Your message has been received. Our wellness team will contact you shortly to confirm your details.">
          <GatheringForm />
        </FormProvider>
      </div>
    </section>;
}
function GatheringForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="p-12 text-center border border-border/50 bg-card/30 backdrop-blur-sm rounded-lg">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 text-primary mb-6">
          <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h3 className="font-default text-2xl mb-4 text-foreground">Message Sent</h3>
        <p className="text-muted-foreground mb-8 font-default">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="font-default uppercase tracking-wider text-xs rounded-full">
          Send Another Message
        </Button>
      </div>;
  }
  const minimalInputClass = "border-0 border-b border-border rounded-none bg-transparent px-0 py-3 h-auto shadow-none focus-visible:ring-0 focus-visible:border-primary text-foreground text-lg transition-colors placeholder:text-muted-foreground/50";
  return <Form onSubmit={handleSubmit} className="space-y-10">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
        <FormField control={control} name="full_name" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-xs uppercase tracking-widest text-muted-foreground font-default">Your Full Name</FormLabel>
              <FormControl>
                <Input placeholder="Jane Doe" className={minimalInputClass} {...field} />
              </FormControl>
              <FormDescription className="text-xs mt-2 font-default">
                As it appears on your insurance policy.
              </FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-xs uppercase tracking-widest text-muted-foreground font-default">Email Address</FormLabel>
              <FormControl>
                <Input type="email" placeholder="jane@example.com" className={minimalInputClass} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-xs uppercase tracking-widest text-muted-foreground font-default">Phone Number</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="(416) 938-9900" className={minimalInputClass} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="event_date" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-xs uppercase tracking-widest text-muted-foreground font-default">Preferred Date</FormLabel>
              <FormControl>
                <Input type="date" className={minimalInputClass} {...field} />
              </FormControl>
              <FormDescription className="text-xs mt-2 font-default">
                Select your desired appointment date.
              </FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="guest_count" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-xs uppercase tracking-widest text-muted-foreground font-default">Number of People</FormLabel>
              <FormControl>
                <Input type="number" min={1} max={10} placeholder="1" className={minimalInputClass} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="event_type" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-xs uppercase tracking-widest text-muted-foreground font-default">Service Interest</FormLabel>
              <FormControl>
                <div className="relative">
                  <select className={`w-full appearance-none outline-none ${minimalInputClass} cursor-pointer font-default`} {...field}>
                    <option value="" disabled className="bg-background text-muted-foreground">
                      Select a service
                    </option>
                    <option value="RMT Massage" className="bg-background text-foreground">RMT Massage</option>
                    <option value="Facial" className="bg-background text-foreground">Facial</option>
                    <option value="Massage (Relaxation)" className="bg-background text-foreground">Massage (Relaxation)</option>
                    <option value="Direct Billing Inquiry" className="bg-background text-foreground">Direct Billing Inquiry</option>
                    <option value="Insurance Question" className="bg-background text-foreground">Insurance Question</option>
                    <option value="Acupuncture" className="bg-background text-foreground">Acupuncture</option>
                    <option value="Other" className="bg-background text-foreground">Other</option>
                  </select>
                  <div className="absolute right-0 top-1/2 -translate-y-1/2 pointer-events-none text-muted-foreground">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="m6 9 6 6 6-6" />
                    </svg>
                  </div>
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="special_requests" render={({
      field
    }) => <FormItem>
            <FormLabel className="text-xs uppercase tracking-widest text-muted-foreground font-default">How Can Our North York Specialists Help You Today?</FormLabel>
            <FormControl>
              <Textarea placeholder="Tell us about your wellness goals or any specific areas of concern..." className={`min-h-[120px] resize-none ${minimalInputClass} font-default`} {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 text-destructive text-sm font-default">
          {submitError}
        </div>}

      <div className="pt-6 flex flex-col items-center text-center">
        <p className="text-xs text-muted-foreground mb-6 max-w-md font-default">
          By submitting this form, you agree to our{" "}
          <Link to="/ContactPage" className="underline hover:text-primary transition-colors">
            Privacy Policy
          </Link>{" "}
          and consent to being contacted regarding your wellness appointment.
        </p>
        
        <div className="flex flex-col md:flex-row items-center gap-6 w-full md:w-auto">
          <Button type="submit" disabled={isSubmitting} size="lg" className="w-full md:w-auto min-w-[240px] h-14 rounded-full font-default uppercase tracking-widest text-sm bg-primary hover:bg-primary/90 text-primary-foreground transition-all shadow-md hover:shadow-lg">
            {isSubmitting ? <>
                <Loader className="animate-spin mr-3 size-5" />
                Sending Message...
              </> : "Send Message"}
          </Button>
          <Link to="tel:416-938-9900" className="text-sm font-default text-muted-foreground hover:text-primary transition-colors">
            Or call us directly at 416-938-9900
          </Link>
        </div>
      </div>
    </Form>;
}